import { Box, Button, Dialog, DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select, Slide, Stack, TextField, Typography } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DatePicker } from "@mui/x-date-pickers";
import { useSnackbar } from "notistack";
import React, { ReactElement } from "react";
import { ApiError } from "../../Api/ApiError";
import { regStoreWorkingParticularSetting } from "../../Api/Store";
import { Time } from "../../Helper/Time";
import TimeInput from "../atoms/TimeInput";


const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: ReactElement<any, any>;
    },
    ref: React.Ref<unknown>,
) {
    return <Slide direction="up" ref={ref} {...props} />
})

export interface WorkingParticularSettingDialogProps {
    showDialog: boolean
    storeId?: number
    onClose: (isEdit: boolean) => void
}

const WrkptcType = {
    "ChangeWorkTime": "ChangeWorkTime",
    "DayOff": "DayOff"
} as const
type WrkptcType = typeof WrkptcType[keyof typeof WrkptcType];

const WorkingParticularSettingDialog: React.FC<WorkingParticularSettingDialogProps> = ({
    showDialog,
    storeId,
    onClose,
}) => {
    let { enqueueSnackbar } = useSnackbar()

    const [open, setOpen] = React.useState<boolean>(false)
    const [wrkptcDate, setWrkptcDate] = React.useState<Date>()
    const [wrkptcType, setWrkptcType] = React.useState<WrkptcType>()
    const [wrkptcOpen, setWrkptcOpen] = React.useState<Time>()
    const [wrkptcClose, setWrkptcClose] = React.useState<Time>()

    const abortController = React.useMemo(() => new AbortController(), [])

    React.useEffect(() => {
        return () => {
            abortController.abort()
        }
    }, [abortController])

    const doSave = React.useCallback(() => {
        if( !wrkptcDate ) {
            enqueueSnackbar("日付が入力されていません", {variant: "error"})
            return
        }
        if( !wrkptcType ) {
            enqueueSnackbar("登録タイプが入力されていません", {variant: "error"})
            return
        }
        if( wrkptcType === WrkptcType.ChangeWorkTime && (!wrkptcOpen || !wrkptcClose) ) {
            enqueueSnackbar("時間が入力されていません", {variant: "error"})
            return
        }
        if (storeId) {
            regStoreWorkingParticularSetting({
                str_id: storeId,
                wrkptc_date: wrkptcDate,
                wrkptc_open: wrkptcType === WrkptcType.ChangeWorkTime ? wrkptcOpen : undefined,
                wrkptc_close: wrkptcType === WrkptcType.ChangeWorkTime ? wrkptcClose : undefined,
            }, {signal: abortController.signal}).then(() => {
                enqueueSnackbar("登録しました", { variant: "success" })
                onClose(true)
            }).catch((e: any) => {
                if( e.__CANCEL__ ) {
                    console.info("canceled")
                } else if (e instanceof ApiError) {
                    enqueueSnackbar(e.message, { variant: "error" })
                } else if (e instanceof Error) {
                    enqueueSnackbar(e.message, { variant: "error" })
                }else {
                    enqueueSnackbar("通信に失敗しました", { variant: "error" })
                }
            })
        }
    }, [abortController, enqueueSnackbar, storeId, wrkptcDate, wrkptcType, wrkptcOpen, wrkptcClose, onClose])

    React.useEffect(() => {
        setOpen(false)
        if( showDialog ) {
            setWrkptcDate(undefined)
            setWrkptcType(undefined)
            setWrkptcOpen(undefined)
            setWrkptcClose(undefined)
            setOpen(true)
        }
    }, [showDialog])
    
    return (
        <Dialog
            open={open}
            TransitionComponent={Transition}
            onClose={() => {
                onClose(false)
            }}
            fullWidth
        >
            <DialogTitle>特定日の営業時間を設定</DialogTitle>
            <DialogContent>
                <Box component="form" onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                    e.preventDefault()
                    doSave()
                    // if( newPassword == confirmPassword && oldPassword.length>0 && newPassword.length>0 ) {
                    //     if( storeId ) {
                    //         EditStorePassword({
                    //             str_id: storeId,
                    //             old_str_login_pass: oldPassword,
                    //             new_str_login_pass: newPassword
                    //         }).then(() => {
                    //             enqueueSnackbar("パスワードを変更しました", { variant: "success" })
                    //         }).catch(() => {
                    //             enqueueSnackbar("変更に失敗しました", { variant: "error" })
                    //         })
                    //     }
                    // } else {
                    //     enqueueSnackbar("パスワードが一致しません", {variant: "error"})
                    // }
                }}>
                    <Stack
                        sx={{
                            mb: 2
                        }}
                        spacing={2}
                    >
                        <DatePicker
                            label="日付"
                            onChange={date => {
                                setWrkptcDate(date ? date : undefined)
                            }}
                            value={wrkptcDate ? wrkptcDate : null}
                            renderInput={params => <TextField {...params} size="small" required />}
                            inputFormat="yyyy/MM/dd"
                            mask="____/__/__"
                            clearable={true}
                            defaultCalendarMonth={null}
                        />
                        <FormControl size="small">
                            <InputLabel id='type-select-label'>登録タイプ</InputLabel>
                            <Select
                                labelId='type-select-label'
                                value={wrkptcType ? wrkptcType : ''}
                                onChange={e => {
                                    setWrkptcType(e.target.value ? e.target.value as WrkptcType : undefined)
                                }}
                                label="登録タイプ"
                                required
                            >
                                <MenuItem value='' disabled />
                                <MenuItem value={WrkptcType.ChangeWorkTime}>営業時間変更</MenuItem>
                                <MenuItem value={WrkptcType.DayOff}>休館日に指定</MenuItem>
                            </Select>
                        </FormControl>
                        {wrkptcType===WrkptcType.ChangeWorkTime &&
                            <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                            >
                                <TimeInput
                                    value={wrkptcOpen ? wrkptcOpen : null}
                                    onChange={newValue => {
                                        setWrkptcOpen(newValue ? newValue : undefined)
                                    }}
                                    required
                                />
                                <Typography>～</Typography>
                                <TimeInput
                                    value={wrkptcClose ? wrkptcClose : null}
                                    onChange={newValue => {
                                        setWrkptcClose(newValue ? newValue : undefined)
                                    }}
                                    required
                                />
                            </Stack>
                        }
                    </Stack>
                    <Stack
                        direction="row"
                        justifyContent="flex-end"
                    >
                        <Button
                            variant="outlined"
                            type="submit"
                        >変更を実行</Button>
                    </Stack>
                </Box>
            </DialogContent>
        </Dialog>
    );
}

export default WorkingParticularSettingDialog